<template>
  <section
    class="lead-new-leads-column ui-kanban-column ui-kanban-column--fixed"
    :aria-busy="initialLoading ? 'true' : 'false'"
  >
    <LeadFlowStageMarker code="unassigned" first />

    <header class="lead-new-leads-column__header ui-kanban-column__header">
      <div class="lead-new-leads-column__title ui-kanban-column__title">
        <strong>Unassigned</strong>
        <AppStatusBadge design="new" compact tone="neutral" :label="String(leadCount)" />
      </div>
      <div class="lead-new-leads-column__description ui-kanban-column__description">
        <p><AppTableText text="Waiting for manager assignment" /></p>
        <LeadFlowSocketStatus v-if="!search" :status="socketStatus" />
      </div>
    </header>

    <AppContentSkeleton v-if="initialLoading" layout="block" label="Loading unassigned leads" />
    <AppErrorState v-else-if="error" message="Unable to load unassigned leads" @retry="loadInitialSnapshot" />
    <div v-else-if="!leads.length" class="lead-new-leads-column__empty ui-kanban-column__empty">
      <img :src="emptyStateIcon" alt="" aria-hidden="true">
      <div>
        <p class="lead-new-leads-column__empty-title ui-kanban-column__empty-title">{{ search ? 'No matching unassigned leads' : 'No unassigned leads' }}</p>
        <p class="lead-new-leads-column__empty-description ui-kanban-column__empty-description">
          {{ search ? 'Try another search term.' : 'Leads will appear here until a manager is assigned.' }}
        </p>
      </div>
    </div>

    <LeadNewLeadCard
      v-for="lead in leads"
      :key="lead.id"
      :lead="lead"
      :now="now"
      :highlighted="Boolean(highlightedLeads[lead.id])"
      @open="$emit('open', $event)"
    />

    <LeadFlowNewLeadsSocket
      v-if="snapshotReady && !search"
      :reconcile="reconcileSnapshot"
      @status-change="socketStatus = $event"
      @created="handleCreatedEvent"
      @assigned="handleAssignedEvent"
    />
  </section>
</template>

<script>
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import LeadFlowNewLeadsSocket from '@/views/crm/task-tracker/LeadFlowNewLeadsSocket.vue'
import LeadFlowSocketStatus from '@/views/crm/task-tracker/LeadFlowSocketStatus.vue'
import LeadFlowStageMarker from '@/views/crm/task-tracker/LeadFlowStageMarker.vue'
import LeadNewLeadCard from '@/views/crm/task-tracker/LeadNewLeadCard.vue'

export default {
  name: 'LeadNewLeadsColumn',
  components: {
    AppContentSkeleton,
    AppErrorState,
    AppTableText,
    LeadFlowNewLeadsSocket,
    LeadFlowSocketStatus,
    LeadFlowStageMarker,
    LeadNewLeadCard
  },
  props: {
    loadLeads: {
      type: Function,
      required: true
    },
    search: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      leads: [],
      leadCount: 0,
      initialLoading: false,
      error: null,
      snapshotReady: false,
      snapshotGeneration: 0,
      socketStatus: 'unavailable',
      highlightedLeads: {},
      highlightTimers: {},
      eventVersions: {},
      assignedLeadIds: {},
      emptyStateIcon: require('@/assets/img/lead-flow/stage-new-leads.png'),
      now: Date.now(),
      waitingTimer: null
    }
  },
  created() {
    this.loadInitialSnapshot()
  },
  mounted() {
    this.waitingTimer = setInterval(() => {
      this.now = Date.now()
    }, 60000)
  },
  beforeDestroy() {
    this.snapshotGeneration += 1
    clearInterval(this.waitingTimer)
    Object.values(this.highlightTimers).forEach(timer => clearTimeout(timer))
  },
  methods: {
    loadInitialSnapshot() {
      if (this.initialLoading) return
      this.loadSnapshot(true).catch(() => {})
    },
    reconcileSnapshot() {
      return this.loadSnapshot(false)
    },
    async loadSnapshot(initial) {
      const generation = this.snapshotGeneration + 1
      this.snapshotGeneration = generation

      if (initial) {
        this.initialLoading = true
        this.error = null
        this.socketStatus = 'unavailable'
      }

      try {
        const response = await this.loadLeads()
        if (generation !== this.snapshotGeneration) return

        const data = response.data || response
        const results = Array.isArray(data.results) ? data.results : []
        this.leads = this.deduplicateLeads(results)
        this.leadCount = Number.isFinite(data.count) ? data.count : this.leads.length
        this.eventVersions = {}
        this.assignedLeadIds = {}
        this.snapshotReady = true
        this.error = null
      } catch (error) {
        if (generation === this.snapshotGeneration && initial) {
          this.error = error
          this.snapshotReady = false
        }
        throw error
      } finally {
        if (generation === this.snapshotGeneration && initial) this.initialLoading = false
      }
    },
    deduplicateLeads(leads) {
      const seenIds = new Set()
      return leads.filter(lead => {
        const id = this.normalizeLeadId(lead && lead.id)
        if (!id || seenIds.has(id)) return false
        seenIds.add(id)
        return true
      })
    },
    handleCreatedEvent(event) {
      const lead = event.data
      const id = this.normalizeLeadId(lead.id)
      if (!id || this.assignedLeadIds[id] || !this.shouldApplyEvent(id, event.occurredAt, 1)) return

      const existingIndex = this.leads.findIndex(item => this.normalizeLeadId(item.id) === id)
      const updatedLeads = this.leads.filter(item => this.normalizeLeadId(item.id) !== id)
      updatedLeads.push(lead)
      this.leads = this.sortLeads(updatedLeads)
      if (existingIndex === -1) {
        this.leadCount += 1
        this.highlightLead(id)
      }
    },
    handleAssignedEvent(event) {
      const id = this.normalizeLeadId(event.data.id)
      if (!id || !this.shouldApplyEvent(id, event.occurredAt, 2)) return

      const wasAssigned = Boolean(this.assignedLeadIds[id])
      this.$set(this.assignedLeadIds, id, true)
      this.removeLead(id)
      if (!wasAssigned) {
        this.$emit('assigned', {
          ...event.data,
          id,
          source: 'websocket'
        })
      }
    },
    shouldApplyEvent(id, occurredAt, priority) {
      const occurredAtTimestamp = new Date(occurredAt).getTime()
      const currentVersion = this.eventVersions[id]
      if (currentVersion) {
        if (occurredAtTimestamp < currentVersion.occurredAt) return false
        if (occurredAtTimestamp === currentVersion.occurredAt && priority <= currentVersion.priority) return false
      }

      this.$set(this.eventVersions, id, {
        occurredAt: occurredAtTimestamp,
        priority
      })
      return true
    },
    sortLeads(leads) {
      return [...leads].sort((left, right) => {
        const leftWaitingAt = this.waitingTimestamp(left)
        const rightWaitingAt = this.waitingTimestamp(right)
        if (leftWaitingAt !== rightWaitingAt) return leftWaitingAt - rightWaitingAt
        return this.normalizeLeadId(left.id) - this.normalizeLeadId(right.id)
      })
    },
    waitingTimestamp(lead) {
      const timestamp = new Date(lead.waiting_since || lead.created_at).getTime()
      return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp
    },
    normalizeLeadId(value) {
      const id = Number(value)
      return Number.isInteger(id) && id > 0 ? id : null
    },
    highlightLead(id) {
      this.$set(this.highlightedLeads, id, true)
      if (this.highlightTimers[id]) clearTimeout(this.highlightTimers[id])
      this.highlightTimers[id] = setTimeout(() => {
        this.$delete(this.highlightedLeads, id)
        delete this.highlightTimers[id]
      }, 2100)
    },
    removeLead(id) {
      const previousLength = this.leads.length
      this.leads = this.leads.filter(item => this.normalizeLeadId(item.id) !== id)
      if (this.leads.length < previousLength) this.leadCount = Math.max(0, this.leadCount - 1)
      this.$delete(this.highlightedLeads, id)
      if (this.highlightTimers[id]) {
        clearTimeout(this.highlightTimers[id])
        delete this.highlightTimers[id]
      }
    }
  }
}
</script>
